import { MANIFESTS } from '@ndrc/app-registry/manifests';
import { CLIENT_LOADERS } from '@ndrc/app-registry/client';
import type { GlyphShape } from '@ndrc/ui';

/**
 * Adaptateur du registre **généré**. Deux raisons d'exister, et pas une de plus :
 *
 * 1. Tant qu'aucune app n'est installée, `MANIFESTS` vaut littéralement `{} as const` et son type
 *    `AppId` vaut `never` : indexer par une chaîne ne compile pas. Ce fichier est le seul endroit
 *    qui fait cette conversion, une fois, avec le commentaire qui l'explique.
 * 2. Le bureau n'importe **jamais** le barrel `@ndrc/app-registry` (qui réexporte aussi
 *    `SERVER_MODULES`, donc le `server.ts` de chaque app, donc Drizzle et Postgres) : seulement
 *    `/manifests` et `/client`. La frontière tient à cette ligne d'import.
 *
 * Le cœur ne nomme jamais une app : tout ce qu'il sait d'elle vient de son manifeste.
 */

/** Ce que la coque lit d'un manifeste. Un sous-ensemble structurel de `AppManifest`
 * (`@ndrc/app-sdk/manifest`) : ni les événements, ni les migrations ne servent à dessiner un dock
 * et une fenêtre. */
export interface DeskManifest {
  workspace?: 'required' | 'none';
  capabilities?: readonly 'interactive'[];
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  /** La marque dessinée par l'app elle-même. Absente : `icon` désigne une forme générique de la
   * trousse du cœur. Le bureau ne sait pas ce que ce dessin représente — il le pose. */
  glyph?: readonly GlyphShape[];
  version: string;
  sdk: string;
  permissions: { uses: readonly string[] };
  /** Les entités du CŒUR que cette app travaille. La coque n'en a besoin que pour une phrase, mais
   * elle est décisive : l'écran de la consigne dit si le bouton « Vérifier » a un rapport avec
   * l'outil du jour, et il le déduit de cette déclaration — jamais d'un `if (appId === …)`
   * (`activity/reviewSubject.ts`, règle 1 de `CLAUDE.md`). */
  entities: { uses: readonly { entity: string }[] };
  ui: { student: { defaultRoute: string } };
}

export type ClientLoader = () => Promise<unknown>;

const manifestsById = MANIFESTS as Readonly<Record<string, DeskManifest | undefined>>;
const loadersById = CLIENT_LOADERS as Readonly<Record<string, ClientLoader | undefined>>;

/** Toutes les apps installées, dans l'ordre alphabétique de leur nom affiché — un ordre stable,
 * qui ne dépend d'aucun ordre d'écriture du fichier généré. */
export const INSTALLED_APPS: readonly DeskManifest[] = Object.values(manifestsById)
  .filter((manifest): manifest is DeskManifest => manifest !== undefined)
  .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

export function findManifest(appId: string): DeskManifest | undefined {
  return Object.hasOwn(manifestsById, appId) ? manifestsById[appId] : undefined;
}

export function findClientLoader(appId: string): ClientLoader | undefined {
  return Object.hasOwn(loadersById, appId) ? loadersById[appId] : undefined;
}
